<template>
  <div
    class="compass-container"
    :class="{ 'compass-expanded': expanded }"
    @click="toggleExpanded"
    :title="expanded ? `${Math.round(heading)}°` : 'Compass'"
  >
    <div class="compass-ring">
      <svg
        viewBox="0 0 100 100"
        class="compass-svg"
        :style="{ transform: `rotate(${-heading}deg)` }"
      >
        <!-- Outer ring -->
        <circle
          cx="50"
          cy="50"
          r="46"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          opacity="0.3"
        />

        <!-- Cardinal directions tick marks -->
        <g stroke="currentColor" stroke-width="2">
          <!-- N -->
          <line x1="50" y1="8" x2="50" y2="16" />
          <!-- E -->
          <line x1="84" y1="50" x2="92" y2="50" />
          <!-- S -->
          <line x1="50" y1="84" x2="50" y2="92" />
          <!-- W -->
          <line x1="8" y1="50" x2="16" y2="50" />
        </g>

        <!-- Intermediate tick marks -->
        <g stroke="currentColor" stroke-width="1" opacity="0.5">
          <!-- NE -->
          <line x1="74" y1="26" x2="78" y2="22" />
          <!-- SE -->
          <line x1="74" y1="74" x2="78" y2="78" />
          <!-- SW -->
          <line x1="26" y1="74" x2="22" y2="78" />
          <!-- NW -->
          <line x1="26" y1="26" x2="22" y2="22" />
        </g>

        <!-- North indicator (red triangle) -->
        <polygon
          points="50,12 45,24 55,24"
          fill="#e74c3c"
        />

        <!-- South indicator -->
        <polygon
          points="50,88 45,76 55,76"
          fill="currentColor"
          opacity="0.5"
        />

        <!-- Cardinal letters (only shown when expanded) -->
        <g v-if="expanded" class="cardinal-letters" font-size="10" fill="currentColor" text-anchor="middle">
          <text x="50" y="32" font-weight="bold" fill="#e74c3c">N</text>
          <text x="75" y="54">E</text>
          <text x="50" y="82">S</text>
          <text x="25" y="54">W</text>
        </g>
      </svg>

      <!-- Center dot (viewing direction indicator) -->
      <div class="compass-center">
        <div class="compass-dot"></div>
      </div>
    </div>

    <!-- Heading display -->
    <div v-if="expanded" class="compass-heading">
      <span class="heading-value">{{ Math.round(heading) }}°</span>
      <span class="heading-direction">{{ cardinalDirection }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'

const props = withDefaults(defineProps<{
  heading: number
  showByDefault?: boolean
}>(), {
  heading: 0,
  showByDefault: false
})

const expanded = ref(props.showByDefault)

const toggleExpanded = () => {
  expanded.value = !expanded.value
}

const cardinalDirection = computed(() => {
  const h = props.heading
  if (h >= 337.5 || h < 22.5) return 'N'
  if (h >= 22.5 && h < 67.5) return 'NE'
  if (h >= 67.5 && h < 112.5) return 'E'
  if (h >= 112.5 && h < 157.5) return 'SE'
  if (h >= 157.5 && h < 202.5) return 'S'
  if (h >= 202.5 && h < 247.5) return 'SW'
  if (h >= 247.5 && h < 292.5) return 'W'
  if (h >= 292.5 && h < 337.5) return 'NW'
  return 'N'
})
</script>

<style scoped>
.compass-container {
  position: relative;
  width: 48px;
  height: 48px;
  cursor: pointer;
  transition: all 0.3s ease;
  user-select: none;
}

.compass-container.compass-expanded {
  width: 80px;
  height: 100px;
}

.compass-ring {
  position: relative;
  width: 48px;
  height: 48px;
  transition: all 0.3s ease;
}

.compass-expanded .compass-ring {
  width: 80px;
  height: 80px;
}

.compass-svg {
  width: 100%;
  height: 100%;
  color: var(--compass-color, #ffffff);
  transition: transform 0.1s ease-out;
}

.compass-center {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 8px;
  height: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.compass-dot {
  width: 6px;
  height: 6px;
  background: var(--compass-dot-color, #3498db);
  border-radius: 50%;
  box-shadow: 0 0 4px var(--compass-dot-color, #3498db);
}

.compass-expanded .compass-center {
  width: 12px;
  height: 12px;
}

.compass-expanded .compass-dot {
  width: 10px;
  height: 10px;
}

.compass-heading {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 4px;
  font-size: 12px;
  color: var(--compass-color, #ffffff);
}

.heading-value {
  font-weight: bold;
  font-size: 14px;
}

.heading-direction {
  font-size: 10px;
  opacity: 0.7;
}

.cardinal-letters text {
  font-family: sans-serif;
}

@media (max-width: 768px) {
  .compass-container {
    width: 36px;
    height: 36px;
  }

  .compass-ring {
    width: 36px;
    height: 36px;
  }

  .compass-container.compass-expanded {
    width: 56px;
    height: 76px;
  }

  .compass-expanded .compass-ring {
    width: 56px;
    height: 56px;
  }

  .compass-heading .heading-value {
    font-size: 11px;
  }

  .compass-heading .heading-direction {
    font-size: 9px;
  }
}
</style>
